import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Globe, Sparkle, Image as ImageIcon, MapPin, ArrowClockwise, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";
import useAiJob from "@/hooks/useAiJob";
import MultiSelectCountries from "@/components/MultiSelectCountries";

// Optional quick-pick region → country lists so admins can select an
// entire region without hunting through 197 checkboxes. Keeps the same
// canonical country names the backend expects.
const REGION_QUICK_PICKS = {
  Africa: [
    "Algeria","Angola","Benin","Botswana","Burkina Faso","Burundi","Cabo Verde","Cameroon",
    "Central African Republic","Chad","Comoros","Democratic Republic of the Congo","Republic of the Congo",
    "Ivory Coast","Djibouti","Egypt","Equatorial Guinea","Eritrea","Eswatini","Ethiopia","Gabon","Gambia",
    "Ghana","Guinea","Guinea-Bissau","Kenya","Lesotho","Liberia","Libya","Madagascar","Malawi","Mali",
    "Mauritania","Mauritius","Morocco","Mozambique","Namibia","Niger","Nigeria","Rwanda",
    "Sao Tome and Principe","Senegal","Seychelles","Sierra Leone","Somalia","South Africa","South Sudan",
    "Sudan","Tanzania","Togo","Tunisia","Uganda","Zambia","Zimbabwe",
  ],
  "East Asia": ["China","Japan","Mongolia","North Korea","South Korea","Taiwan"],
  "South Asia": ["Afghanistan","Bangladesh","Bhutan","India","Maldives","Nepal","Pakistan","Sri Lanka"],
  "Southeast Asia": ["Brunei","Cambodia","Indonesia","Laos","Malaysia","Myanmar","Philippines","Singapore","Thailand","Timor-Leste","Vietnam"],
  "Middle East": ["Bahrain","Iran","Iraq","Israel","Jordan","Kuwait","Lebanon","Oman","Palestine","Qatar","Saudi Arabia","Syria","Turkey","United Arab Emirates","Yemen"],
  Europe: [
    "Albania","Andorra","Austria","Belarus","Belgium","Bosnia and Herzegovina","Bulgaria","Croatia","Cyprus",
    "Czech Republic","Denmark","Estonia","Finland","France","Germany","Greece","Hungary","Iceland","Ireland",
    "Italy","Kosovo","Latvia","Liechtenstein","Lithuania","Luxembourg","Malta","Moldova","Monaco","Montenegro",
    "Netherlands","North Macedonia","Norway","Poland","Portugal","Romania","Russia","San Marino","Serbia",
    "Slovakia","Slovenia","Spain","Sweden","Switzerland","Ukraine","United Kingdom","Vatican City",
  ],
  "North America": ["Canada","Mexico","United States"],
  "Central America & Caribbean": [
    "Antigua and Barbuda","Bahamas","Barbados","Belize","Costa Rica","Cuba","Dominica","Dominican Republic",
    "El Salvador","Grenada","Guatemala","Haiti","Honduras","Jamaica","Nicaragua","Panama",
    "Saint Kitts and Nevis","Saint Lucia","Saint Vincent and the Grenadines","Trinidad and Tobago",
  ],
  "South America": ["Argentina","Bolivia","Brazil","Chile","Colombia","Ecuador","Guyana","Paraguay","Peru","Suriname","Uruguay","Venezuela"],
  Oceania: ["Australia","Fiji","Kiribati","Marshall Islands","Micronesia","Nauru","New Zealand","Palau","Papua New Guinea","Samoa","Solomon Islands","Tonga","Tuvalu","Vanuatu"],
  "Central Asia": ["Kazakhstan","Kyrgyzstan","Tajikistan","Turkmenistan","Uzbekistan"],
};

const PAGE_SIZE = 60;

/**
 * WorldRecipesPanel — Admin AI-batch tool for world recipes. Includes
 * multi-country selection, paginated "all recipes" view, and a
 * duplicate-detection panel so the admin can spot & prune accidental
 * clones across World + Family + manual recipes.
 */
export default function WorldRecipesPanel() {
  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(true);

  // Paginated recipes list
  const [recipes, setRecipes] = useState([]);
  const [recipesTotal, setRecipesTotal] = useState(0);
  const [recipesLoading, setRecipesLoading] = useState(false);
  const [recipesHasMore, setRecipesHasMore] = useState(false);

  // Duplicate groups
  const [dupGroups, setDupGroups] = useState([]);
  const [dupLoading, setDupLoading] = useState(false);
  const [showDupes, setShowDupes] = useState(false);

  const [count, setCount] = useState(50);
  const [focusCountries, setFocusCountries] = useState([]);
  const [generateImages, setGenerateImages] = useState(true);

  const loadCoverage = useCallback(async () => {
    setCoverageLoading(true);
    try {
      const { data } = await api.get("/admin/world-recipes/coverage");
      setCoverage(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load coverage");
    } finally {
      setCoverageLoading(false);
    }
  }, []);

  const loadRecipes = useCallback(async ({ append = false } = {}) => {
    setRecipesLoading(true);
    try {
      const skip = append ? recipes.length : 0;
      const { data } = await api.get("/admin/world-recipes/recent", {
        params: { limit: PAGE_SIZE, skip },
      });
      setRecipesTotal(data.total || 0);
      setRecipesHasMore(!!data.has_more);
      setRecipes((prev) => (append ? [...prev, ...(data.items || [])] : data.items || []));
    } catch (e) {
      /* silent */
    } finally {
      setRecipesLoading(false);
    }
  }, [recipes.length]);

  const loadDuplicates = useCallback(async () => {
    setDupLoading(true);
    try {
      const { data } = await api.get("/admin/world-recipes/duplicates");
      setDupGroups(data.groups || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load duplicates");
    } finally {
      setDupLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCoverage();
    loadRecipes();
    loadDuplicates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Map recipe_id → duplicate-group signature for quick badge lookup.
  const dupByRecipeId = useMemo(() => {
    const map = {};
    dupGroups.forEach((g) => {
      g.recipes.forEach((r) => {
        map[r.recipe_id] = { count: g.count, signature: g.signature, titles: g.titles };
      });
    });
    return map;
  }, [dupGroups]);

  const job = useAiJob({
    statusUrl: (id) => `/admin/world-recipes/generate/status/${id}`,
    onDone: (data) => {
      const ins = data?.inserted ?? 0;
      const sk = data?.skipped ?? 0;
      toast.success(`Done — inserted ${ins}, skipped ${sk} duplicates.`);
      loadCoverage();
      loadRecipes();
      loadDuplicates();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e?.message || "Batch failed"),
    onCancelled: () => {
      toast("Batch cancelled");
      loadCoverage();
      loadRecipes();
      loadDuplicates();
    },
  });

  const startBatch = async () => {
    try {
      await job.start(() =>
        api
          .post("/admin/world-recipes/generate", {
            count: Number(count) || 50,
            focus_countries: focusCountries.length ? focusCountries : undefined,
            generate_images: generateImages,
          })
          .then((r) => r.data),
      );
    } catch (_) { /* toast handled */ }
  };

  const deleteRecipe = async (recipeId, title) => {
    if (!window.confirm(`Delete "${title}"?\n\nThis removes it from the cookbook permanently.`)) return;
    try {
      await api.delete(`/recipes/${recipeId}?permanent=1`);
      toast.success("Deleted");
      loadRecipes();
      loadDuplicates();
      loadCoverage();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const progress = job.progress;
  const running = progress && progress.status === "running";

  const percent = useMemo(() => {
    if (!progress || !progress.total) return 0;
    return Math.min(100, Math.round((progress.processed / progress.total) * 100));
  }, [progress]);

  const coveragePercent = coverage
    ? Math.round((coverage.covered_count / coverage.total_world_countries) * 100)
    : 0;

  return (
    <div className="space-y-8" data-testid="world-recipes-panel">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-rf-amber/20 flex items-center justify-center flex-shrink-0">
          <Globe size={26} weight="duotone" className="text-rf-amber-dark" />
        </div>
        <div>
          <h2 className="font-display text-2xl md:text-3xl font-black text-rf-ink">World Recipes</h2>
          <p className="text-sm text-rf-ink-soft mt-1 max-w-2xl">
            Draft fully-formed recipes (title, metric ingredients, detailed steps, tools, tags + optional AI hero image)
            in batches of up to 100. New recipes land in the <b>Inbox</b> cookbook <code className="text-xs bg-rf-cream px-1.5 py-0.5 rounded">cb_2016975e4b1d</code>
            {" "}for review, edit and publish. All go in as public and copyright-certified with the <b>RecipeFiler</b> tag.
          </p>
        </div>
      </div>

      {/* Coverage stats */}
      <div className="rounded-2xl border border-rf-ink/10 bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MapPin size={20} weight="duotone" className="text-rf-ink-soft" />
            <h3 className="font-display text-lg font-black text-rf-ink">Country coverage</h3>
          </div>
          <button
            onClick={loadCoverage}
            disabled={coverageLoading}
            data-testid="coverage-refresh"
            className="text-xs font-bold text-rf-ink-soft hover:text-rf-ink flex items-center gap-1 disabled:opacity-50"
          >
            <ArrowClockwise size={14} /> Refresh
          </button>
        </div>
        {coverageLoading && !coverage ? (
          <p className="text-sm text-rf-ink-soft">Loading…</p>
        ) : coverage ? (
          <>
            <div className="flex items-end gap-3 mb-2">
              <span className="font-display text-4xl font-black text-rf-ink" data-testid="coverage-covered-count">
                {coverage.covered_count}
              </span>
              <span className="text-lg text-rf-ink-soft mb-1">/ {coverage.total_world_countries} countries</span>
              <span className="ml-auto text-sm text-rf-ink-soft">{coveragePercent}% · {coverage.recipes_in_cookbook} recipes in cookbook</span>
            </div>
            <div className="w-full h-2 rounded-full bg-rf-cream overflow-hidden mb-4">
              <div className="h-full bg-rf-amber transition-all" style={{ width: `${coveragePercent}%` }} />
            </div>
            <details className="text-sm">
              <summary className="cursor-pointer font-bold text-rf-ink-soft hover:text-rf-ink">
                Missing countries ({coverage.missing_count})
              </summary>
              <div className="mt-3 flex flex-wrap gap-1.5 max-h-64 overflow-y-auto p-3 bg-rf-cream/50 rounded-lg">
                {coverage.missing.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => {
                      if (!focusCountries.includes(c)) setFocusCountries([...focusCountries, c]);
                      toast(`Added ${c} to focus list`);
                    }}
                    data-testid={`missing-${c.replace(/ /g, "-").toLowerCase()}`}
                    className="inline-flex items-center px-2 py-0.5 rounded-full bg-white border border-rf-ink/10 text-xs text-rf-ink-soft hover:bg-rf-amber/20 hover:text-rf-ink"
                  >
                    {c}
                  </button>
                ))}
              </div>
            </details>
          </>
        ) : null}
      </div>

      {/* Batch controls */}
      <div className="rounded-2xl border border-rf-ink/10 bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkle size={20} weight="duotone" className="text-rf-amber-dark" />
          <h3 className="font-display text-lg font-black text-rf-ink">Generate a new batch</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <label className="block">
            <span className="text-xs font-bold text-rf-ink-soft uppercase tracking-wide">Batch size</span>
            <input
              type="number"
              min="1"
              max="100"
              value={count}
              onChange={(e) => setCount(e.target.value)}
              data-testid="world-batch-count"
              disabled={running}
              className="mt-1.5 w-full rounded-lg border border-rf-ink/15 px-3 py-2 text-rf-ink focus:outline-none focus:border-rf-amber disabled:opacity-50"
            />
            <span className="text-[11px] text-rf-ink-soft mt-1 block">1-100. Chunks of 5 in the background.</span>
          </label>

          <label className="block">
            <span className="text-xs font-bold text-rf-ink-soft uppercase tracking-wide">Countries (multi-select)</span>
            <MultiSelectCountries
              value={focusCountries}
              onChange={setFocusCountries}
              options={coverage?.covered ? [...coverage.covered, ...coverage.missing] : []}
              regions={REGION_QUICK_PICKS}
              placeholder="Any (auto-prioritise gaps)"
              disabled={running}
              testId="world-batch-countries"
            />
            <span className="text-[11px] text-rf-ink-soft mt-1 block">Leave empty to let AI auto-prioritise missing countries.</span>
          </label>

          <label className="block">
            <span className="text-xs font-bold text-rf-ink-soft uppercase tracking-wide">AI hero image</span>
            <div className="mt-1.5 flex items-center gap-3 rounded-lg border border-rf-ink/15 px-3 py-2 bg-white">
              <input
                type="checkbox"
                checked={generateImages}
                onChange={(e) => setGenerateImages(e.target.checked)}
                data-testid="world-batch-images"
                disabled={running}
                className="w-4 h-4 accent-rf-amber"
              />
              <span className="text-sm text-rf-ink flex items-center gap-1.5">
                <ImageIcon size={16} weight="duotone" className="text-rf-ink-soft" />
                Generate + activate hero photo
              </span>
            </div>
            <span className="text-[11px] text-rf-ink-soft mt-1 block">Runs per-recipe in the background.</span>
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={startBatch}
            disabled={running || job.busy}
            data-testid="world-batch-start"
            className="px-5 py-2.5 rounded-full bg-rf-amber text-rf-ink font-bold text-sm hover:bg-rf-amber-dark disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            <Sparkle size={16} weight="fill" />
            {running ? "Running…" : `Draft ${count} recipes`}
          </button>
          {running && (
            <button
              onClick={job.cancel}
              data-testid="world-batch-cancel"
              className="px-4 py-2.5 rounded-full bg-white text-rf-ink font-bold text-sm border border-rf-ink/15 hover:bg-rf-cream"
            >
              Cancel
            </button>
          )}
        </div>

        {progress && (
          <div className="mt-6 rounded-xl bg-rf-cream/60 border border-rf-ink/10 p-4" data-testid="world-batch-progress">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="font-bold text-rf-ink">
                Status: <span className="uppercase">{progress.status}</span>
              </span>
              <span className="text-rf-ink-soft">
                Chunk {progress.processed || 0} / {progress.total || 0}
              </span>
            </div>
            <div className="w-full h-2 rounded-full bg-white overflow-hidden mb-3">
              <div className="h-full bg-rf-amber transition-all" style={{ width: `${percent}%` }} />
            </div>
            <div className="flex flex-wrap gap-4 text-xs text-rf-ink-soft">
              <span><b className="text-rf-ink">{progress.inserted || 0}</b> inserted</span>
              <span><b className="text-rf-ink">{progress.skipped || 0}</b> duplicates skipped</span>
              <span>Target: <b className="text-rf-ink">{progress.requested || count} recipes</b></span>
              {progress.focus_countries && Array.isArray(progress.focus_countries) && (
                <span>Countries: <b className="text-rf-ink">{progress.focus_countries.length}</b></span>
              )}
            </div>
            {progress.error && (
              <p className="mt-3 text-xs text-red-600">Error: {progress.error}</p>
            )}
          </div>
        )}
      </div>

      {/* Duplicate warning banner (always visible when any exist) */}
      {dupGroups.length > 0 && (
        <div className="rounded-2xl border-2 border-red-300 bg-red-50 p-5" data-testid="world-dup-banner">
          <div className="flex items-start gap-3">
            <Warning size={22} weight="duotone" className="text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-display font-black text-red-900">
                {dupGroups.length} duplicate {dupGroups.length === 1 ? "title" : "titles"} detected
              </p>
              <p className="text-sm text-red-800 mt-0.5">
                Recipes with matching normalised titles have been created across World Recipes, Family Recipes or manual entries.
                Expand below to review and delete the extras.
              </p>
              <button
                onClick={() => setShowDupes((v) => !v)}
                data-testid="world-dup-toggle"
                className="mt-3 px-3 py-1.5 rounded-full bg-red-600 text-white text-xs font-bold hover:bg-red-700"
              >
                {showDupes ? "Hide" : "Review duplicates"}
              </button>
            </div>
          </div>

          {showDupes && (
            <div className="mt-5 space-y-4">
              {dupGroups.map((g) => (
                <div key={g.signature} className="rounded-xl bg-white border border-red-200 p-4" data-testid={`world-dup-group-${g.signature.replace(/ /g, "-")}`}>
                  <p className="text-xs font-bold uppercase tracking-wide text-red-700 mb-1">
                    {g.count} entries · signature: {g.signature}
                  </p>
                  <p className="text-sm text-rf-ink mb-3">Titles: <span className="italic">{g.titles.join(" · ")}</span></p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {g.recipes.map((r) => (
                      <div key={r.recipe_id} className="flex items-center gap-2 rounded-lg bg-rf-cream/50 border border-rf-ink/10 p-2 text-xs">
                        {r.ai_image_url ? (
                          <img src={r.ai_image_url} alt="" className="w-10 h-10 object-cover rounded" loading="lazy" />
                        ) : (
                          <div className="w-10 h-10 rounded bg-white border border-rf-ink/10" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-rf-ink truncate">{r.title}</p>
                          <p className="text-rf-ink-soft truncate">
                            {r.source} · {(r.family_country || r.world_country || "—")} · {r.created_at?.slice(0, 10)}
                          </p>
                        </div>
                        <a
                          href={`/recipe/${r.recipe_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-rf-ink-soft hover:text-rf-ink underline text-[11px]"
                        >
                          view
                        </a>
                        <button
                          onClick={() => deleteRecipe(r.recipe_id, r.title)}
                          data-testid={`world-dup-delete-${r.recipe_id}`}
                          className="text-[11px] font-bold text-red-600 hover:text-red-800"
                        >
                          delete
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* All recipes (paginated) */}
      <div className="rounded-2xl border border-rf-ink/10 bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-black text-rf-ink">
            AI world recipes <span className="text-rf-ink-soft font-normal">({recipesTotal})</span>
          </h3>
          <button
            onClick={() => { loadRecipes(); loadDuplicates(); }}
            disabled={recipesLoading || dupLoading}
            data-testid="world-recipes-refresh"
            className="text-xs font-bold text-rf-ink-soft hover:text-rf-ink flex items-center gap-1 disabled:opacity-50"
          >
            <ArrowClockwise size={14} /> Refresh
          </button>
        </div>
        {recipes.length === 0 && !recipesLoading ? (
          <p className="text-sm text-rf-ink-soft">No AI world recipes in the target cookbook yet.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="world-recipes-grid">
              {recipes.map((r) => {
                const dup = dupByRecipeId[r.recipe_id];
                return (
                  <a
                    key={r.recipe_id}
                    href={`/recipe/${r.recipe_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="block rounded-xl overflow-hidden border border-rf-ink/10 hover:border-rf-amber transition-colors bg-rf-cream relative"
                    data-testid={`world-recipe-item-${r.recipe_id}`}
                  >
                    {dup && (
                      <span
                        className="absolute top-1.5 right-1.5 z-10 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-600 text-white text-[9px] font-black uppercase tracking-wide shadow"
                        title={`Duplicate title also appears ${dup.count - 1} other time(s)`}
                        data-testid={`world-recipe-dup-badge-${r.recipe_id}`}
                      >
                        <Warning size={9} weight="fill" /> Dup
                      </span>
                    )}
                    <div className="aspect-square bg-rf-cream flex items-center justify-center relative">
                      {r.ai_image_url ? (
                        <img src={r.ai_image_url} alt={r.title} className="w-full h-full object-cover" loading="lazy" />
                      ) : r.ai_image_status === "generating" ? (
                        <div className="text-[10px] font-bold text-rf-ink-soft flex flex-col items-center gap-1">
                          <Sparkle size={20} className="text-rf-amber animate-pulse" />
                          Generating
                        </div>
                      ) : r.ai_image_status === "failed" ? (
                        <div className="text-[10px] font-bold text-red-500 px-2 text-center">Image failed</div>
                      ) : (
                        <ImageIcon size={22} className="text-rf-ink-soft/40" />
                      )}
                    </div>
                    <div className="p-2.5">
                      <p className="text-xs font-bold text-rf-ink line-clamp-2">{r.title}</p>
                      <p className="text-[10px] text-rf-ink-soft mt-0.5">
                        {r.world_country || (r.tags || [])[0] || "—"}
                        {r.difficulty && ` · ${r.difficulty}`}
                      </p>
                    </div>
                  </a>
                );
              })}
            </div>
            {recipesHasMore && (
              <div className="mt-6 flex justify-center">
                <button
                  onClick={() => loadRecipes({ append: true })}
                  disabled={recipesLoading}
                  data-testid="world-recipes-load-more"
                  className="px-5 py-2.5 rounded-full bg-white text-rf-ink font-bold text-sm border border-rf-ink/15 hover:bg-rf-cream disabled:opacity-50"
                >
                  {recipesLoading ? "Loading…" : `Load more (${recipesTotal - recipes.length} left)`}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
